import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "./App.css";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import Home from "./pages/Home";
import Gallery from "./pages/Gallery";
import Category from "./pages/Category";
import ArtworkDetail from "./pages/ArtworkDetail";
import Artists from "./pages/Artists";
import ArtistProfile from "./pages/ArtistProfile";
import ArtCenters from "./pages/ArtCenters";
import ArtCenterProfile from "./pages/ArtCenterProfile";
import Login from "./pages/Login";
import Register from "./pages/Register";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import About from "./pages/About";
import Auctions from "./pages/Auctions";
import AuctionDetail from "./pages/AuctionDetail";
import ArtistOfTheWeek from "./pages/ArtistOfTheWeek";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminCenters from "./pages/admin/AdminCenters";
import AdminAudit from "./pages/admin/AdminAudit";
import AdminFlyer from "./pages/admin/AdminFlyer";
import AdminRooms from "./pages/admin/AdminRooms";
import AdminIntroVideos from "./pages/admin/AdminIntroVideos";
import AdminCatalogue from "./pages/admin/AdminCatalogue";
import AdminContent from "./pages/admin/AdminContent";
import AdminArticles from "./pages/admin/AdminArticles";
import AdminEvents from "./pages/admin/AdminEvents";
import AdminSocial from "./pages/admin/AdminSocial";
import AdminArtistOfWeek from "./pages/admin/AdminArtistOfWeek";
import AdminFeatured from "./pages/admin/AdminFeatured";
import AdminOrdering from "./pages/admin/AdminOrdering";
import Journal from "./pages/Journal";
import Events from "./pages/Events";
import ContentPage from "./pages/ContentPage";
import { SettingsProvider } from "./context/SettingsContext";
import SiteBanner from "./components/SiteBanner";
import { useTrackVisit } from "./hooks/useTrackVisit";

/** Tiny wrapper so useTrackVisit (which needs router context) can mount. */
function AnalyticsTracker() {
  useTrackVisit();
  return null;
}

/** Legacy /art-center/:id → /art-hub/:id redirect (v1.39 rename). */
function LegacyHubRedirect() {
  const { id } = useParams();
  return <Navigate to={`/art-hub/${id}`} replace />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <HelmetProvider>
          <AuthProvider>
            <SettingsProvider>
              <AnalyticsTracker />
              <Header />
              <SiteBanner />
              <main className="min-h-[60vh]">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/gallery" element={<Gallery />} />
                <Route path="/category/:slug" element={<Category />} />
                <Route path="/artwork/:id" element={<ArtworkDetail />} />
                <Route path="/artists" element={<Artists />} />
                <Route path="/artist/:id" element={<ArtistProfile />} />
                <Route path="/art-hubs" element={<ArtCenters />} />
                <Route path="/art-hub/:id" element={<ArtCenterProfile />} />
                {/* Legacy — kept so old bookmarks / social shares don't 404. */}
                <Route path="/art-centers" element={<Navigate to="/art-hubs" replace />} />
                <Route path="/art-center/:id" element={<LegacyHubRedirect />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/artist-of-the-week" element={<ArtistOfTheWeek />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/checkout/success" element={<CheckoutSuccess />} />
                <Route path="/about" element={<About />} />
                <Route path="/journal" element={<Journal />} />
                <Route path="/journal/:slug" element={<ContentPage kind="post" />} />
                <Route path="/events" element={<Events />} />
                <Route path="/p/:slug" element={<ContentPage kind="page" />} />
                <Route path="/auctions" element={<Auctions />} />
                <Route path="/auction/:id" element={<AuctionDetail />} />
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="plugins" element={<AdminSettings />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="art-hubs" element={<AdminCenters />} />
                  <Route path="art-centers" element={<Navigate to="/admin/art-hubs" replace />} />
                  <Route path="audit" element={<AdminAudit />} />
                  <Route path="flyer" element={<AdminFlyer />} />
                  <Route path="rooms" element={<AdminRooms />} />
                  <Route path="intro-videos" element={<AdminIntroVideos />} />
                  <Route path="catalogue" element={<AdminCatalogue />} />
                  <Route path="content" element={<AdminContent />} />
                  <Route path="articles" element={<AdminArticles />} />
                  <Route path="events" element={<AdminEvents />} />
                  <Route path="social" element={<AdminSocial />} />
                  <Route path="artist-of-the-week" element={<AdminArtistOfWeek />} />
                  <Route path="featured" element={<AdminFeatured />} />
                  <Route path="ordering" element={<AdminOrdering />} />
                </Route>
              </Routes>
            </main>
            <Footer />
            <Toaster position="bottom-right" />
            </SettingsProvider>
          </AuthProvider>
        </HelmetProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
